// One kind's template: version history, MJML editor, preview (desktop /
// mobile), save draft, send test to me, publish. The preview is rendered by
// notification-center with the worker's own renderer, so it is what is sent.
import React, {useCallback, useEffect, useState} from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack,
  TextField, ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material';

import {getTemplate, previewTemplate, publishTemplate, saveDraft, sendTestEmail} from '../../actions/notifications';
import {commonVariables, errorMessage, jsonObjectError, prettyJson, statusColor} from '../../lib/notifications';

const WIDTH = {desktop: 640, mobile: 380};
const mono = {fontFamily: 'Menlo, Consolas, monospace', fontSize: 13};

const formOf = (v) => ({
  subject: (v && v.subject) || '',
  mjmlSource: (v && v.mjmlSource) || '',
  textSource: (v && v.textSource) || '',
  sampleDataJson: prettyJson((v && v.sampleDataJson) || '{}')
});

const TemplateEditor = ({kind, onBack}) => {
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(formOf(null));
  const [dirty, setDirty] = useState(false);
  const [preview, setPreview] = useState(null);
  const [width, setWidth] = useState('desktop');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [confirmPublish, setConfirmPublish] = useState(false);

  const load = useCallback(async (version) => {
    setBusy('load');
    setError('');
    try {
      const res = await getTemplate(kind.kind, version);
      setDetail(res);
      setForm(formOf(res.selected));
      setDirty(false);
      setPreview(null);
    } catch (e) {
      setError(errorMessage(e));
    }
    setBusy('');
  }, [kind.kind]);

  useEffect(() => { load(); }, [load]);

  const selected = detail && detail.selected;
  const sampleError = jsonObjectError(form.sampleDataJson);
  const edit = (field) => (e) => {
    setForm({...form, [field]: e.target.value});
    setDirty(true);
  };

  const run = async (name, fn) => {
    setBusy(name);
    setError('');
    setInfo('');
    try {
      await fn();
    } catch (e) {
      setError(errorMessage(e));
    }
    setBusy('');
  };

  // Unsaved edits preview the draft text; otherwise the stored version.
  const doPreview = () => run('preview', async () => {
    const source = dirty || !selected
      ? {draft: {subject: form.subject, mjmlSource: form.mjmlSource, ...(form.textSource ? {textSource: form.textSource} : {})}}
      : {version: selected.version};
    setPreview(await previewTemplate(kind.kind, source, sampleError ? undefined : form.sampleDataJson));
  });

  const doSave = () => run('save', async () => {
    const res = await saveDraft(kind.kind, form);
    await load(res.version);
    setInfo(`Saved as draft version ${res.version}.`);
  });

  const doTest = () => run('test', async () => {
    const res = await sendTestEmail(kind.kind, selected.version);
    setInfo(`Test email sent to your address (provider id ${res.providerMessageId}).`);
  });

  const doPublish = () => run('publish', async () => {
    setConfirmPublish(false);
    await publishTemplate(kind.kind, selected.version);
    await load(selected.version);
    setInfo(`Version ${selected.version} is now published. Emails sent from now on use it.`);
  });

  const canPublish = selected && selected.status === 'draft' && !dirty;
  const canTest = selected && !dirty;

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2, flexWrap: 'wrap', rowGap: 1}}>
        <Button onClick={onBack}>&larr; All templates</Button>
        <Typography variant="h6">{kind.kind}</Typography>
        <Chip size="small" label={kind.category} color={kind.category === 'marketing' ? 'secondary' : 'primary'} />
        {detail && detail.versions && detail.versions.length > 0 && (
          <TextField select size="small" label="Version" value={selected ? selected.version : ''}
            onChange={(e) => load(Number(e.target.value))} sx={{minWidth: 220}}>
            {detail.versions.map((v) => (
              <MenuItem key={v.version} value={v.version}>v{v.version} — {v.status}</MenuItem>
            ))}
          </TextField>
        )}
        {selected && <Chip size="small" label={selected.status} color={statusColor(selected.status)} />}
        {dirty && <Chip size="small" label="unsaved changes" color="warning" variant="outlined" />}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>{kind.description}</Typography>
      <Stack direction="row" spacing={0.5} sx={{mb: 2, flexWrap: 'wrap', rowGap: 0.5}}>
        {(kind.requiredVariables || []).map((v) => <Chip key={v} size="small" label={`{{ ${v} }}`} />)}
        {(kind.optionalVariables || []).map((v) => <Chip key={v} size="small" variant="outlined" label={`{{ ${v} }} optional`} />)}
        {commonVariables(kind.category).map((v) => <Chip key={v} size="small" variant="outlined" color="info" label={`{{ ${v} }} filled by us`} />)}
      </Stack>

      {error && <Alert severity="error" sx={{mb: 2}} onClose={() => setError('')}>{error}</Alert>}
      {info && <Alert severity="success" sx={{mb: 2}} onClose={() => setInfo('')}>{info}</Alert>}
      {busy === 'load' && <CircularProgress size={24} />}

      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', lg: '1fr 1fr'}, gap: 2}}>
        <Stack spacing={2}>
          <TextField label="Subject" value={form.subject} onChange={edit('subject')} size="small" fullWidth />
          <TextField label="MJML body" value={form.mjmlSource} onChange={edit('mjmlSource')} multiline minRows={16}
            maxRows={30} fullWidth inputProps={{style: mono, spellCheck: false}} />
          <TextField label="Plain-text body (optional — derived from the HTML when empty)" value={form.textSource}
            onChange={edit('textSource')} multiline minRows={3} fullWidth inputProps={{style: mono}} />
          <TextField label="Sample data (JSON, used by previews)" value={form.sampleDataJson}
            onChange={edit('sampleDataJson')} multiline minRows={4} fullWidth inputProps={{style: mono}}
            error={!!sampleError} helperText={sampleError || ' '} />
          <Stack direction="row" spacing={1} sx={{flexWrap: 'wrap', rowGap: 1}}>
            <Button variant="outlined" onClick={doPreview} disabled={!!busy}>
              {busy === 'preview' ? <CircularProgress size={18} /> : 'Preview'}
            </Button>
            <Button variant="contained" onClick={doSave} disabled={!!busy || !dirty || !!sampleError}>
              {busy === 'save' ? <CircularProgress size={18} /> : 'Save as draft'}
            </Button>
            <Button onClick={doTest} disabled={!!busy || !canTest} title="Sends the selected version to your own email address">
              {busy === 'test' ? <CircularProgress size={18} /> : 'Send test to me'}
            </Button>
            <Button color="success" variant="contained" onClick={() => setConfirmPublish(true)} disabled={!!busy || !canPublish}>
              Publish
            </Button>
          </Stack>
        </Stack>

        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 1}}>
            <Typography variant="subtitle2">Preview</Typography>
            <ToggleButtonGroup size="small" exclusive value={width} onChange={(e, v) => v && setWidth(v)}>
              <ToggleButton value="desktop">Desktop</ToggleButton>
              <ToggleButton value="mobile">Mobile</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          {preview ? (
            <>
              <Typography variant="body2" sx={{mb: 1}}><b>Subject:</b> {preview.subject}</Typography>
              <Box sx={{border: '1px solid #ddd', width: WIDTH[width], maxWidth: '100%', overflow: 'hidden'}}>
                {/* sandbox with no permissions: no scripts, no navigation */}
                <iframe title="Email preview" sandbox="" srcDoc={preview.html}
                  style={{width: '100%', height: 640, border: 0, display: 'block'}} />
              </Box>
              <Typography variant="caption" color="text.secondary">Plain-text part</Typography>
              <Box component="pre" sx={{...mono, whiteSpace: 'pre-wrap', background: '#f6f6f6', p: 1, m: 0}}>{preview.text}</Box>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">Press Preview to render with the sample data.</Typography>
          )}
        </Box>
      </Box>

      <Dialog open={confirmPublish} onClose={() => setConfirmPublish(false)}>
        <DialogTitle>Publish version {selected && selected.version}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Every {kind.kind} email sent from now on uses this version, including ones already waiting in the queue.
            The current published version is archived.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPublish(false)}>Cancel</Button>
          <Button color="success" variant="contained" onClick={doPublish}>Publish</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateEditor;
